import { useEffect, useMemo, useState } from "react"
import { createFileRoute, Link } from "@tanstack/react-router"
import { ArrowRight, Building2, CalendarDays, MapPin, ShieldCheck } from "lucide-react"
import { useEvents } from "@/core/active-event"
import { useUserOrgs } from "@/core/orgs"
import { isControlPlaneUser } from "@/core/auth"
import type { MyEventRow } from "@/core/types"
import { requireSession } from "@/lib/auth-guard"

export const Route = createFileRoute("/")({
  beforeLoad: ({ location }) => requireSession(location.href),
  component: Landing,
})

// Org-less root portal. One section per org the user administers, each with its
// events; pick an event to enter the workspace. Membership-driven (admin-events).
function Landing() {
  const events = useEvents((s) => s.events)
  const loading = useEvents((s) => s.loading)
  const loaded = useEvents((s) => s.loaded)
  const error = useEvents((s) => s.error)
  const loadEvents = useEvents((s) => s.load)
  const clearOrg = useEvents((s) => s.clearOrg)
  const orgs = useUserOrgs((s) => s.orgs)
  const orgsLoaded = useUserOrgs((s) => s.loaded)
  const orgsLoading = useUserOrgs((s) => s.loading)
  const loadOrgs = useUserOrgs((s) => s.load)

  useEffect(() => {
    if (!loaded && !loading) void loadEvents()
  }, [loaded, loading, loadEvents])
  useEffect(() => {
    if (!orgsLoaded && !orgsLoading) void loadOrgs()
  }, [orgsLoaded, orgsLoading, loadOrgs])
  // Root must be org-less: drop any active org from a prior visit.
  useEffect(() => {
    void clearOrg()
  }, [clearOrg])

  const sections = useMemo(() => {
    const byOrg = new Map<string, MyEventRow[]>()
    for (const e of events) {
      if (!e.organizationId) continue
      const arr = byOrg.get(e.organizationId) ?? []
      arr.push(e)
      byOrg.set(e.organizationId, arr)
    }
    return orgs
      .map((o) => ({ org: o, events: byOrg.get(o.id) ?? [] }))
      .sort((a, b) =>
        (a.events.length > 0) !== (b.events.length > 0)
          ? a.events.length > 0
            ? -1
            : 1
          : a.org.name.localeCompare(b.org.name),
      )
  }, [events, orgs])

  const stillLoading = (loading && !loaded) || (orgsLoading && !orgsLoaded)

  return (
    <div className="app-canvas min-h-dvh">
      <div className="mx-auto max-w-4xl px-6 py-10">
        <header className="mb-8 flex items-center gap-3">
          <img
            src="https://files.attend.vip/logo/logo_extra-small.svg"
            alt="Attend"
            className="h-3 w-auto dark:brightness-0 dark:invert"
          />
          <div className="flex-1">
            <h1 className="text-[20px] font-semibold tracking-tightish text-plum-900 dark:text-plum-50">
              Your events
            </h1>
            <p className="text-[13px] text-plum-700/55 dark:text-plum-200/45">
              Pick an event to open the console.
            </p>
          </div>
          <PlatformLink />
        </header>

        {stillLoading ? (
          <div className="space-y-8">
            {[0, 1].map((s) => (
              <section key={s}>
                <div className="mb-2 flex items-center gap-2 px-1">
                  <Building2 className="h-3.5 w-3.5 text-plum-700/45 dark:text-plum-200/35" />
                  <div className="h-3 w-28 animate-pulse rounded bg-plum-100 dark:bg-white/10" />
                </div>
                <ul className="grid gap-2.5 sm:grid-cols-2">
                  {[0, 1].map((i) => (
                    <li key={i}>
                      <div className="flex items-center gap-3 rounded-2xl border border-plum-200/60 bg-white p-3.5 shadow-fluent-sm ring-1 ring-plum-200/20 dark:border-white/10 dark:bg-surface-raised dark:ring-white/5">
                        <span className="h-11 w-11 shrink-0 animate-pulse rounded-xl bg-plum-100 dark:bg-white/10" />
                        <span className="min-w-0 flex-1 space-y-1.5">
                          <span className="block h-3.5 w-32 animate-pulse rounded bg-plum-100 dark:bg-white/10" />
                          <span className="block h-3 w-20 animate-pulse rounded bg-plum-100 dark:bg-white/10" />
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-[13px] text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">
            Couldn't load events: {error}
          </div>
        ) : sections.length === 0 ? (
          <div className="rounded-2xl border border-plum-200/60 bg-white p-8 text-center shadow-fluent ring-1 ring-plum-200/30 dark:border-white/10 dark:bg-surface-raised dark:ring-white/5">
            <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-plum-100/70 text-plum-700/70 dark:bg-white/5 dark:text-plum-200/55">
              <Building2 className="h-5 w-5" />
            </div>
            <p className="text-[14px] font-semibold text-plum-900 dark:text-plum-50">
              Nothing to manage yet
            </p>
            <p className="mt-1 text-[13px] text-plum-700/55 dark:text-plum-200/45">
              This account isn't a member of any organizations.
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {sections.map(({ org, events: evs }) => (
              <section key={org.id}>
                <div className="mb-2 flex items-center gap-2 px-1">
                  <Building2 className="h-3.5 w-3.5 text-plum-700/45 dark:text-plum-200/35" />
                  <h2 className="text-[11px] font-bold tracking-[0.07em] text-plum-700/55 uppercase dark:text-plum-200/40">
                    {org.name}
                  </h2>
                </div>
                {evs.length === 0 ? (
                  <p className="px-1 text-[12.5px] text-plum-700/45 dark:text-plum-200/35">
                    No events yet.
                  </p>
                ) : (
                  <ul className="grid gap-2.5 sm:grid-cols-2">
                    {evs.map((e) => (
                      <EventCard key={e.eventId} event={e} />
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function EventCard({ event: e }: { event: MyEventRow }) {
  const place = e.addressLocality ?? e.venueName ?? null
  return (
    <li>
      <Link
        to="/$orgSlug/$eventSlug"
        params={{ orgSlug: e.organizationSlug ?? "", eventSlug: e.slug }}
        className="group flex items-center gap-3 rounded-2xl border border-plum-200/60 bg-white p-3.5 shadow-fluent-sm ring-1 ring-plum-200/20 transition hover:shadow-fluent dark:border-white/10 dark:bg-surface-raised dark:ring-white/5"
      >
        <span className="grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-xl bg-gradient-to-br from-plum-500 to-plum-800 text-[13px] font-bold text-white">
          {e.coverImageUrl ? (
            <img src={e.coverImageUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            initials(e.name)
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[14px] font-semibold text-plum-900 dark:text-plum-50">
            {e.name}
          </span>
          <span className="mt-0.5 flex items-center gap-2 text-[11.5px] text-plum-700/55 dark:text-plum-200/40">
            {e.startsAt && (
              <span className="flex items-center gap-1">
                <CalendarDays className="h-3 w-3" />
                {fmtDate(e.startsAt)}
              </span>
            )}
            {place && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {place}
              </span>
            )}
          </span>
        </span>
        <ArrowRight className="h-4 w-4 shrink-0 text-plum-700/30 transition group-hover:translate-x-0.5 group-hover:text-plum-700/60 dark:text-plum-200/30" />
      </Link>
    </li>
  )
}

// Control-plane shortcut into the platform console (appmanager + sysadmin).
// Gated off the JWT `userRole` claim; renders nothing for everyone else.
function PlatformLink() {
  const [show, setShow] = useState(false)
  useEffect(() => {
    let alive = true
    void isControlPlaneUser().then((ok) => {
      if (alive && ok) setShow(true)
    })
    return () => {
      alive = false
    }
  }, [])
  if (!show) return null
  return (
    <Link
      to="/platform"
      className="inline-flex items-center gap-1.5 rounded-xl border border-plum-200/60 bg-white px-3 py-1.5 text-[12.5px] font-semibold text-plum-700 shadow-fluent-sm transition hover:text-plum-900 dark:border-white/10 dark:bg-surface-raised dark:text-plum-200/70 dark:hover:text-plum-50"
    >
      <ShieldCheck className="h-3.5 w-3.5" /> Platform
    </Link>
  )
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

function fmtDate(v: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v)
  if (!m) return ""
  return `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}`
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return "?"
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
}
